/**
 * My interests (INVENTORY §1 screen 5, §4.3; prototype lines 242-312).
 *
 * BOARD mode is one of the two long-press drag surfaces. The DOM contract the
 * gesture hit-tests against is load-bearing:
 *   row      `data-gt-zone="int"` + `data-gt-row="s|a|b|c"`
 *   panel    `data-gt-zone="int"` + `data-gt-row="int-todo"`
 *   capsule  `data-gt-cap="{id}"`
 * `lib/drag.ts` reads those attributes out of `document.elementFromPoint`, so
 * renaming either breaks the drop silently. The four call rows carry their own
 * letter; the TO-DO panel carries `INT_TODO_ROW`, never `''`, because an empty
 * attribute is one the hit-test cannot see. The drag machinery itself never
 * reads the row list, so none of the board's reshapings needed a
 * `lib/drag.ts` change.
 *
 * TO DO (DESIGN-CHANGELOG 2026-08-24) replaced the fifth `?` NO CALL YET row:
 * uncalled games queue one at a time above the rows, in phone geometry, the
 * mobile GOTY block's twin. Dropping a called capsule on it clears the call.
 * Unlike desktop, the phone keeps its other route back to uncalled — the move
 * sheet's `? NO CALL YET` act.
 *
 * Tap vs. hold: a capsule tap opens the move sheet, but only when
 * `drag.suppressed()` is false — the click that follows a drop is swallowed for
 * 450 ms (INVENTORY §7). LIST mode's four letter buttons call `vote`, which is
 * a TOGGLE — re-tapping the filled square clears back to no call — and
 * deliberately does not touch `intOrder` (see the ListSlice header).
 */

import type { PointerEventHandler } from 'react';

import { SearchBox } from '../components/SearchBox.tsx';
import { CoverArt } from '../components/CoverArt.tsx';
import { PageBar } from '../components/PageBar.tsx';
import { TodoThumb } from '../components/TodoThumb.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useDrag } from '../lib/drag.ts';
import type { GameId } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { interestsVM, INT_SKIP_LAST, INT_TODO_LEGEND } from '../store/selectors.ts';
import type { IntBoardRow, IntListRow } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/**
 * The two art call sites are their own components so `useArt` is one hook per
 * tile instead of a hook inside a `.map`. Kinds follow INVENTORY §11: the board
 * capsule is the 2/3 portrait (`coverBg` → `'p'`), the list thumbnail is the
 * 460×215 header (`hdrBg` → `'w'`).
 */
function BoardCapsule({
  g,
  onTap,
  onPointerDown,
}: {
  g: IntBoardRow['games'][number];
  onTap: () => void;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
}): JSX.Element {
  const art = useArt(g.id + 'p', g.coverCands);

  return (
    <div
      onClick={onTap}
      onPointerDown={onPointerDown}
      data-gt-cap={g.key}
      style={{
        position: 'relative',
        width: 104,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border-2)',
        background: 'var(--gt-tile)',
        cursor: 'pointer',
        filter: g.filt,
        transform: 'translateX(' + g.shiftX + ')',
        opacity: g.dragOp,
        transition: 'transform .15s ease',
        userSelect: 'none',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: g.fall, overflow: 'hidden' }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: g.sheen,
            mixBlendMode: 'screen',
            pointerEvents: 'none',
          }}
        />
      </div>
      <div
        style={{
          padding: '6px 7px',
          fontFamily: MONO,
          fontSize: '8.5px',
          color: 'var(--gt-text-3)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {g.nameUp}
      </div>
    </div>
  );
}

function ListRow({ g }: { g: IntListRow }): JSX.Element {
  // Narrow subscriptions, unlike the screen: a row must not re-render on every
  // store write just to paint one thumbnail.
  const openGame = useQd((s) => s.openGame);
  const vote = useQd((s) => s.vote);
  const art = useArt(g.id + 'w', g.hdrCands);

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        border: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        padding: '8px 10px',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 76,
          flex: 'none',
          aspectRatio: '460/215',
          background: g.fall,
          overflow: 'hidden',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={8} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '12.5px',
            color: 'var(--gt-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.name}
        </div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: 'var(--gt-faint)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.metaTxt}
        </div>
      </div>
      {/* The whole ladder on every row, not just the call you made: the filled
          square answers "what did I say?" and the outlines "what else could I
          say?" in one glance. `data-tip` is upstream's markup and stays — the
          tooltip ignores non-mouse pointers, so it costs nothing here. */}
      <div style={{ display: 'flex', gap: 6 }}>
        {g.calls.map((c) => (
          <span
            key={c.key}
            onClick={(e) => {
              e.stopPropagation();
              void vote(g.id, c.key);
            }}
            data-tip={c.tip}
            style={{
              width: 44,
              height: 44,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: MONO,
              fontSize: 13,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {c.g}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Interests(): JSX.Element {
  const s = useQd();
  const v = interestsVM(s);

  // Same art fetch the other party-list screens run — see `screens/Tonight.tsx`.
  useQueueArt(s.list.map((e) => e.id));

  const drag = useDrag({
    zone: 'int',
    dragging: !!s.drag,
    dragOver: s.dragOver && s.dragOver.zone === 'int' ? s.dragOver : null,
    onStart: (row, id) => {
      const g = s.gById(id);
      s.dragStart({ zone: 'int', row, id, name: g ? g.n.toUpperCase() : '' });
    },
    onOver: (ov) => s.setDragOver(ov),
    onDrop: () => s.dragEnd(),
    onCancel: () => s.dragCancel(),
  });

  const tapCap = (id: GameId): void => {
    if (drag.suppressed()) return;
    s.openSheet('int', id);
  };

  // The featured cover's art, fetched at the top level like the GOTY board's —
  // one hook, unconditional, harmless when there is no featured game (id 0 and
  // no candidates fetch nothing).
  const featArt = useArt(v.iFeat.id + 'p', v.iFeat.coverCands);

  /** `iSkip`: the last game left to call cannot be rotated away from. */
  const skip = (): void => {
    if (!v.hasIFeat) return;
    if (v.iSkipBlocked) {
      s.toast(INT_SKIP_LAST);
      return;
    }
    void s.intSkip(v.iFeat.id);
  };

  return (
    <div
      data-screen-label="My interests"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        // Full screen since DESIGN-CHANGELOG 2026-09-03: the tab bar and its
        // spacer are gone, so the page claims the safe-area inset itself, the
        // way GAME DETAIL does.
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* the page's own bar: `◂ MY GAMES` · title + count · BOARD / LIST ---- */}
      <PageBar
        title="MY INTERESTS"
        sub={v.intCountTxt + ' ON THE LIST'}
        right={
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10 }}>
          <div
            onClick={() => s.setIMode('board')}
            style={{
              border: '1px solid ' + v.ibBd,
              color: v.ibClr,
              background: v.ibBg,
              padding: '0 12px',
              height: 40,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
            }}
          >
            ▦ BOARD
          </div>
          <div
            onClick={() => s.setIMode('list')}
            style={{
              // Longhands, not `border` + `borderLeft:0`: the prototype's CSS
              // pair makes React warn about mixing shorthand and longhand when
              // the colour changes on re-render. Same pixels, no warning.
              borderTop: '1px solid ' + v.ilBd,
              borderRight: '1px solid ' + v.ilBd,
              borderBottom: '1px solid ' + v.ilBd,
              borderLeft: 0,
              color: v.ilClr,
              background: v.ilBg,
              padding: '0 12px',
              height: 40,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
            }}
          >
            ≡ LIST
          </div>
        </div>
        }
      />

      {/* THE BOX SITS ABOVE THE FILTER RAIL, full width and 44px tall — the rail
          scrolls horizontally, and a field inside a horizontal scroller is not
          reachable with a thumb. Box then filter, the desktop order read top to
          bottom rather than left to right (DESIGN-CHANGELOG 2026-08-28). */}
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('interests', e.target.value)}
          onClear={() => s.setBq('interests', '')}
          placeholder="SEARCH MY INTERESTS…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
      </div>
      {/* filter bar ------------------------------------------------------ */}
      <div data-hs="1" style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', padding: '12px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: MONO,
            fontSize: '10.5px',
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '9px 11px',
              whiteSpace: 'nowrap',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
            }}
          >
            {c.l}
            <span onClick={() => removeChip(s, c)} style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}>
              ✕
            </span>
          </div>
        ))}
      </div>

      {/* BOARD ----------------------------------------------------------- */}
      {v.iBoard ? (
        <>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.04em', color: 'var(--gt-faint)', padding: '12px 14px 0' }}>
            TAP FOR OPTIONS · HOLD + DRAG TO MOVE OR REORDER
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 14px 26px' }}>
            {/* TO DO — the GOTY block's twin, above the rows it feeds ------ */}
            {v.hasIFeat ? (
              <div
                data-gt-zone="int"
                data-gt-row={v.iTodoRow}
                style={{ display: 'flex', flexDirection: 'column', border: v.iTodoBd, background: v.iTodoBg }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '11px 12px',
                    borderBottom: '1px dashed var(--gt-border-3)',
                  }}
                >
                  <div style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text-5)', transform: 'translateY(0.13em)' }}>
                    TO DO
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>ONE AT A TIME</div>
                  <div style={{ flex: 1 }} />
                  <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--acc2)' }}>{v.iLeftTxt}</div>
                </div>
                <div style={{ display: 'flex', gap: 12, padding: 12 }}>
                  <div
                    onClick={() => s.openGame(v.iFeat.id)}
                    style={{
                      position: 'relative',
                      width: 104,
                      flex: 'none',
                      aspectRatio: '2/3',
                      border: '1px solid var(--gt-border-2)',
                      background: v.iFeat.fall,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      alignSelf: 'flex-start',
                    }}
                  >
                    <CoverArt
                      style={{ position: 'absolute', inset: 0 }}
                      fs={13}
                      ini={v.iFeat.ini}
                      hasCover={featArt.hasCover}
                      cover={featArt.cover}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: v.iFeat.sheen,
                        mixBlendMode: 'screen',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div
                      onClick={() => s.openGame(v.iFeat.id)}
                      style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25, color: 'var(--gt-text)', cursor: 'pointer' }}
                    >
                      {v.iFeat.name}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{v.iFeat.metaTxt}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                      {v.iBtns.map((b) => (
                        <div
                          key={b.key}
                          onClick={() => void s.voteSet(b.id, b.key)}
                          style={{
                            height: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: PIXEL,
                            fontSize: 11,
                            border: '1px solid ' + b.bd,
                            color: b.clr,
                            background: b.bg,
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                          }}
                        >
                          <span style={{ transform: 'translateY(0.13em)' }}>{b.t}</span>
                        </div>
                      ))}
                    </div>
                    <div
                      onClick={skip}
                      style={{
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 7,
                        border: '1px solid var(--gt-border-2)',
                        color: 'var(--gt-text-5)',
                        fontFamily: MONO,
                        fontSize: 10,
                        letterSpacing: '.05em',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      SKIP FOR NOW ↻
                    </div>
                  </div>
                </div>
                <div style={{ padding: '0 12px 11px', fontFamily: MONO, fontSize: 8, color: 'var(--gt-tick)', lineHeight: 1.8 }}>
                  {INT_TODO_LEGEND.map((line, i) => (
                    <span key={line}>
                      {i ? <br /> : null}
                      {line}
                    </span>
                  ))}
                </div>
                {v.hasIUpNext ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      borderTop: '1px dashed var(--gt-border)',
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>
                      UP NEXT
                    </div>
                    {v.iUpNext.map((n) => (
                      <TodoThumb key={n.key} n={n} w={34} fs={7} />
                    ))}
                    {v.hasIMore ? (
                      <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-tick)' }}>{v.iMoreTxt}</div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* all called — still the clear-my-call target, or the last call
                made would have nothing to drag back TO ---------------------- */}
            {v.iAllDone ? (
              <div
                data-gt-zone="int"
                data-gt-row={v.iTodoRow}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  border: v.iTodoBd,
                  background: v.iTodoBg,
                  padding: '26px 16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--acc)' }}>✓</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', lineHeight: 1.9 }}>{v.iDoneTxt}</div>
              </div>
            ) : null}

            {v.intBoardRows.map((r) => (
              <div key={r.key} style={{ display: 'flex', flexDirection: 'column', border: r.bord, background: 'var(--gt-card)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: r.labBg,
                    borderBottom: '1px solid var(--gt-border)',
                    padding: '9px 12px',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--gt-font-pixel)',
                      fontSize: 11,
                      color: r.bigClr,
                      transform: 'translateY(0.13em)',
                    }}
                  >
                    {r.big}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.1em', color: 'var(--gt-text-5)' }}>
                    {r.sub}
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{r.cntTxt}</div>
                </div>
                <div
                  data-hs="1"
                  data-gt-zone="int"
                  data-gt-row={r.rowId}
                  style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: 12 }}
                >
                  {r.games.map((g) => (
                    <BoardCapsule
                      key={g.key}
                      g={g}
                      onTap={() => tapCap(g.id)}
                      onPointerDown={drag.capPD(r.rowId, g.id)}
                    />
                  ))}
                  {r.isEmpty ? (
                    <div
                      style={{
                        alignSelf: 'center',
                        fontFamily: MONO,
                        fontSize: 10,
                        color: 'var(--gt-ghost)',
                        padding: '14px 2px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      NOTHING HERE YET
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* LIST ------------------------------------------------------------ */}
      {v.iList ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>
            <span>ALL GAMES · {v.intCountTxt}</span>
            <div style={{ flex: 1, borderTop: '1px dashed var(--gt-border)' }} />
            <span>TAP S · A · B · C</span>
          </div>
          {v.intRows.map((g) => (
            <ListRow key={g.key} g={g} />
          ))}
        </div>
      ) : null}

      {v.intRows.length === 0 ? (
        <div
          style={{
            padding: '36px 20px',
            textAlign: 'center',
            fontFamily: MONO,
            fontSize: 11,
            color: 'var(--gt-muted)',
            lineHeight: 2,
          }}
        >
          {v.tEmptyTxt}
        </div>
      ) : null}
    </div>
  );
}
